import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { CordovaService } from './cordova.service';
import { PostLoginService } from './post-login.service';
import { createPkcePair } from '../auth/pkce';

/**
 * District SSO login orchestration (#80).
 *
 * Opens the district SSO web flow carrying only a PKCE challenge + client id. The
 * web callback signs the user in, issues a one-time exchange code, and redirects
 * back to the app — only the opaque code rides the URL. The app exchanges that code
 * (plus its secret verifier) at /api/sso/exchange for the same JWT + refresh a
 * password login yields, then re-enters the shared post-login sequence for full
 * parity. A step-up return (mid-session) skips the post-login sequence and puts the
 * user back where they were.
 *
 * Two platforms, two return paths:
 *
 *  - NATIVE (android/ios): the flow opens in the SYSTEM browser — Google blocks
 *    OAuth in embedded webviews — and returns over the custom URL scheme
 *    (com.rackcoach.mobile://sso?code=...), delivered by
 *    cordova-plugin-customurlscheme to window.handleOpenURL.
 *
 *  - WEB (the hosted `browser` build, and `ng serve`): there is no OS scheme
 *    handler, so handleOpenURL never fires and a custom-scheme redirect would
 *    strand the user. Instead the CURRENT TAB navigates to the flow and the server
 *    returns to Sso:WebReturnUrl — this app's own #/auth/sso-return route — where
 *    SsoReturnComponent hands the URL back to handleDeepLink(). Same PKCE, same
 *    exchange, same post-login sequence; only the launch and the return differ.
 *
 * The server tells the two apart by client id, and each has its own allow-list and
 * return target (see SsoMobileHandoff.ClassifyClient) so neither can be sent
 * somewhere it can't receive.
 */
@Injectable({ providedIn: 'root' })
export class SsoService {
  private baseUrl = environment.baseUrl;
  private readonly nativeClientId = 'rackCoachMobile';
  private readonly webClientId = 'rackCoachWeb';
  private readonly verifierKey = 'sso_pkce_verifier';
  // Which flow started the pending hand-off. A step-up return must NOT re-run the
  // post-login sequence — the user is mid-session and would be bounced to /calendar.
  private readonly flowKey = 'sso_flow';
  // Web only: where a step-up left from. Navigating the tab away loses the router
  // state a native step-up keeps by staying alive in the background.
  private readonly returnPathKey = 'sso_return_path';
  private exchangeInFlight = false;

  constructor(
    private http: HttpClient,
    private cordovaService: CordovaService,
    private authService: AuthService,
    private postLogin: PostLoginService,
    private router: Router,
    private zone: NgZone) { }

  /**
   * True only on a real native Cordova build. The hosted `browser` platform also
   * sets environment.cordova and fires deviceready, so the environment flag can't
   * tell them apart — cordova.platformId can. Defaulting anything unrecognised to
   * WEB is the safe direction: the web return path works in a native webview if it
   * ever came to that, whereas a custom-scheme return in a browser dead-ends after
   * the user has already signed in.
   */
  private isNativePlatform(): boolean {
    const platform = this.cordovaService.getPlatform();
    return platform === 'android' || platform === 'ios';
  }

  /** Begin an SSO login: generate PKCE, then open /Sso/Start for this platform. */
  async startLogin(): Promise<void> {
    const url = await this.buildHandoffUrl(`${this.baseUrl}/Sso/Start`, 'login');
    this.launch(url);
  }

  /**
   * Begin a step-up against the #78 step_up_url (an enforced school the session is
   * trying to reach). Same PKCE hand-off appended so the completed step-up returns
   * a code the app can exchange.
   */
  async startStepUp(stepUpUrl: string): Promise<void> {
    const url = await this.buildHandoffUrl(stepUpUrl, 'stepup');
    this.launch(url);
  }

  /**
   * Handle a return URL carrying the exchange code. On native this is fired by
   * cordova-plugin-customurlscheme OUTSIDE Angular's zone, so all work runs inside
   * zone.run() to keep change detection + routing alive; on web SsoReturnComponent
   * calls it with window.location.href. A link with no code, or arriving with no
   * pending verifier (a stale or replayed link), is ignored.
   */
  handleDeepLink(url: string): void {
    const code = this.extractCode(url);
    if (!code) { return; }
    if (this.exchangeInFlight) { return; }
    const verifier = localStorage.getItem(this.verifierKey);
    if (!verifier) {
      // Native: stay silent. The plugin can hand us a stale link long after the
      // fact and yanking a signed-in user to the login screen would be worse than
      // doing nothing. Web: we are sitting on a blank #/auth/sso-return route with
      // nowhere else to go, so say what happened instead of hanging forever.
      if (!this.isNativePlatform()) {
        this.zone.run(() => this.failToLogin(
          'Your school sign-in couldn\'t be completed because it was started in a ' +
          'different browser or tab. Please try again from this window.'));
      }
      return;
    }
    const flow = localStorage.getItem(this.flowKey);
    const returnPath = localStorage.getItem(this.returnPathKey);
    // Burn the verifier BEFORE the exchange, not in the callbacks: the plugin can
    // deliver the same URL more than once (e.g. on resume), and a second pass with
    // the verifier still present would re-exchange an already-used code, get
    // invalid_grant, and clobber the sign-in that just succeeded.
    localStorage.removeItem(this.verifierKey);
    localStorage.removeItem(this.flowKey);
    localStorage.removeItem(this.returnPathKey);
    this.exchangeInFlight = true;

    this.zone.run(() => {
      this.exchange(code, verifier).subscribe(
        (result: any) => {
          this.exchangeInFlight = false;
          this.authService.storeToken(result.access_token);
          if (result.refresh_token) {
            this.authService.storeRefreshToken(result.refresh_token);
          }
          if (flow === 'stepup') {
            // Mid-session step-up: the refreshed token now carries the asserted
            // district, and retrying the action that 403'd succeeds. Re-running the
            // post-login sequence here would re-open school selection and bounce
            // them to /calendar, discarding where they were.
            //
            // Native stayed alive behind the system browser, so "where they were"
            // is still on screen and there is nothing to do. Web navigated the tab
            // away, so put them back on the route they left from.
            if (returnPath) {
              this.router.navigateByUrl(returnPath);
            }
            return;
          }
          // Fall back to the login screen if school selection is dismissed: on web
          // the tab is parked on #/auth/sso-return, a spinner with no way out, and
          // on native we are already on the login page anyway. A page they can act
          // on beats a spinner that never resolves.
          this.postLogin.run('/auth/login');
        },
        (err) => {
          this.exchangeInFlight = false;
          if (flow === 'stepup' && this.isNativePlatform()) {
            // The session is still signed in — don't dump the user on the login
            // screen. The next attempt at the gated action 403s again and
            // re-offers step-up.
            console.warn('SSO step-up exchange failed', err);
            return;
          }
          if (flow === 'stepup' && returnPath) {
            // Same reasoning on web, but the tab is parked on the return route, so
            // it still has to go somewhere: back where the step-up started.
            console.warn('SSO step-up exchange failed', err);
            this.router.navigateByUrl(returnPath);
            return;
          }
          this.failToLogin(this.describeError(err));
        });
    });
  }

  /** Open the hand-off URL the way this platform can come back from. */
  private launch(url: string): void {
    if (this.isNativePlatform()) {
      this.cordovaService.openLinkInSystemBrowser(url);
      return;
    }
    // Same tab, deliberately. A popup would complete the exchange in the popup —
    // which then has the tokens and the opener doesn't — and popup blockers fire on
    // the async path here anyway, since the PKCE await breaks the user-gesture
    // chain.
    window.location.assign(url);
  }

  private async buildHandoffUrl(base: string, flow: 'login' | 'stepup'): Promise<string> {
    const pair = await createPkcePair();
    // Persist the verifier: the system browser backgrounds the app (native) or the
    // tab navigates away entirely (web), so an in-memory field wouldn't survive
    // either. Cleared on redemption.
    localStorage.setItem(this.verifierKey, pair.verifier);
    localStorage.setItem(this.flowKey, flow);
    if (flow === 'stepup' && !this.isNativePlatform()) {
      localStorage.setItem(this.returnPathKey, this.router.url);
    } else {
      localStorage.removeItem(this.returnPathKey);
    }
    const sep = base.indexOf('?') >= 0 ? '&' : '?';
    return base + sep
      + 'client_id=' + encodeURIComponent(this.isNativePlatform() ? this.nativeClientId : this.webClientId)
      + '&code_challenge=' + encodeURIComponent(pair.challenge);
  }

  private failToLogin(message: string): void {
    this.router.navigate(['/auth/login'], { state: { ssoError: message } });
  }

  private exchange(code: string, verifier: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/sso/exchange`, {
      code: code,
      code_verifier: verifier,
    });
  }

  private extractCode(url: string): string {
    if (!url) { return null; }
    const q = url.indexOf('?');
    if (q < 0) { return null; }
    const params = url.substring(q + 1).split('&');
    for (const p of params) {
      const eq = p.indexOf('=');
      if (eq < 0) { continue; }
      if (p.substring(0, eq) === 'code') {
        const raw = p.substring(eq + 1);
        try { return decodeURIComponent(raw); } catch { return raw; }
      }
    }
    return null;
  }

  private describeError(err: any): string {
    const desc = err && err.error && err.error.error_description;
    return desc || 'We couldn\'t complete your school sign-in. Please try again.';
  }
}
